import { useState } from 'react';
import { Sun, Moon, PenTool, Cpu } from 'lucide-react';
import { useThemeStore, ThemeType, ThemeMode } from '../stores/themeStore';

/**
 * ThemeSwitcher — floating instrument panel (bottom-right)
 *
 * Controls two dimensions:
 *   • theme: blueprint | typewriter
 *   • mode:  dark | light
 *
 * Styled as a compact instrument toggle — not a settings modal,
 * not a cookie-cutter dark-mode button. Think "control panel knob panel."
 */
export default function ThemeSwitcher() {
  const { theme, mode, setTheme, setMode } = useThemeStore();
  const [expanded, setExpanded] = useState(false);

  const themes: { id: ThemeType; label: string; icon: React.ReactNode; desc: string }[] = [
    {
      id: 'blueprint',
      label: 'Blueprint',
      icon: <Cpu size={13} />,
      desc: 'Technical drawing',
    },
    {
      id: 'typewriter',
      label: 'Typewriter',
      icon: <PenTool size={13} />,
      desc: 'Vintage workshop',
    },
  ];

  const modes: { id: ThemeMode; icon: React.ReactNode; label: string }[] = [
    { id: 'dark',  icon: <Moon size={13} />,  label: 'Dark'  },
    { id: 'light', icon: <Sun  size={13} />,  label: 'Light' },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '1.25rem',
        right: '1.25rem',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '0.5rem',
        fontFamily: 'var(--font-label)',
      }}
    >
      {/* Expanded control panel */}
      {expanded && (
        <div
          style={{
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            padding: '0.75rem',
            minWidth: '160px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
          }}
        >
          {/* Section: MODE */}
          <div style={{ marginBottom: '0.6rem' }}>
            <div
              style={{
                fontSize: '0.55rem',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: 'var(--color-text-faint)',
                marginBottom: '0.35rem',
                paddingBottom: '0.25rem',
                borderBottom: '1px solid var(--color-border)',
              }}
            >
              ── Mode
            </div>
            <div style={{ display: 'flex', gap: '0.35rem' }}>
              {modes.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMode(m.id)}
                  title={m.label}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.3rem',
                    padding: '0.35rem 0.5rem',
                    fontSize: '0.6rem',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    border: `1px solid ${mode === m.id ? 'var(--color-accent)' : 'var(--color-border)'}`,
                    backgroundColor: mode === m.id ? 'var(--color-accent-dim)' : 'transparent',
                    color: mode === m.id ? 'var(--color-accent)' : 'var(--color-text-muted)',
                    cursor: 'pointer',
                    borderRadius: '1px',
                    transition: 'all 0.12s ease',
                  }}
                >
                  {m.icon}
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Section: THEME */}
          <div>
            <div
              style={{
                fontSize: '0.55rem',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: 'var(--color-text-faint)',
                marginBottom: '0.35rem',
                paddingBottom: '0.25rem',
                borderBottom: '1px solid var(--color-border)',
              }}
            >
              ── Theme
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {themes.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTheme(t.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.35rem 0.6rem',
                    fontSize: '0.6rem',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    border: `1px solid ${theme === t.id ? 'var(--color-accent)' : 'var(--color-border)'}`,
                    backgroundColor: theme === t.id ? 'var(--color-accent-dim)' : 'transparent',
                    color: theme === t.id ? 'var(--color-accent)' : 'var(--color-text-muted)',
                    cursor: 'pointer',
                    borderRadius: '1px',
                    transition: 'all 0.12s ease',
                    textAlign: 'left',
                  }}
                >
                  {t.icon}
                  <span>{t.label}</span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: '0.5rem',
                      color: 'var(--color-text-faint)',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {t.desc}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Toggle button — the knob */}
      <button
        onClick={() => setExpanded((v) => !v)}
        title="Theme controls"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.4rem',
          padding: '0.4rem 0.75rem',
          fontSize: '0.6rem',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          backgroundColor: 'var(--color-surface)',
          color: expanded ? 'var(--color-accent)' : 'var(--color-text-muted)',
          border: `1px solid ${expanded ? 'var(--color-accent)' : 'var(--color-border)'}`,
          cursor: 'pointer',
          borderRadius: '1px',
          fontFamily: 'var(--font-label)',
          boxShadow: expanded ? '0 0 8px var(--color-accent-dim)' : 'none',
          transition: 'all 0.15s ease',
        }}
      >
        {mode === 'dark' ? <Moon size={12} /> : <Sun size={12} />}
        <span>{theme === 'blueprint' ? 'BP' : 'TW'}</span>
        <span style={{ color: 'var(--color-text-faint)', fontSize: '0.5rem' }}>
          {expanded ? '▲' : '▼'}
        </span>
      </button>
    </div>
  );
}
